import isEqual from 'lodash/isEqual';
import { validateDesignPresets } from './presets';
import type {
	DesignAsset,
	DesignAssetSet,
	DesignDefinition,
	DesignModeGroup,
	DesignModeSelection,
	DesignToken,
	DesignTokenSet,
} from './types';
import {
	createDesignModeValue,
	DEFAULT_DESIGN_MODE_GROUPS,
	getDefaultDesignModeSelection,
	resolveDesignModeValue,
	validateDesignModeGroups,
	validateDesignModeValue,
} from './modes';
import { normalizeDesignTokenValue } from './values';

export const SYSTEM_DEFAULT_FONT_FAMILY = 'Assistant, sans-serif';
export const EMPTY_DESIGN_TOKENS: DesignTokenSet = { schemaVersion: 1, collections: [] };
export const EMPTY_DESIGN_ASSETS: DesignAssetSet = { schemaVersion: 1, assets: [] };
const ID = /^[a-z][a-z0-9-]*$/;
export const tokenIdToCssVariable = (id: string): string => `--sc-token-${id}`;
export const dimensionTokenIdToCssVariable = (id: string, platform: string): string =>
	`${tokenIdToCssVariable(id)}--${platform}-vw`;
export const assetIdToReference = (id: string): string => `asset(${id})`;

export function hasDesignTokensOrAssets(design?: DesignDefinition): boolean {
	return Boolean(
		design?.tokens.collections.some(collection => collection.tokens.length > 0) || design?.assets.assets.length
	);
}

export function normalizeDefaultFontFamily(value?: string): string {
	const normalized = value?.trim();
	return normalized && !/[;{}<>\n\r]/.test(normalized) ? normalized : SYSTEM_DEFAULT_FONT_FAMILY;
}

export function validateDesignTokenSet(
	input: unknown,
	modeGroups: DesignModeGroup[] = DEFAULT_DESIGN_MODE_GROUPS
): DesignTokenSet {
	if (
		!input ||
		typeof input !== 'object' ||
		(input as DesignTokenSet).schemaVersion !== 1 ||
		!Array.isArray((input as DesignTokenSet).collections)
	)
		throw new Error('A token set requires schemaVersion: 1 and a collections array.');
	const set = input as DesignTokenSet;
	const ids = new Set<string>();
	const collections = new Set<string>();
	const references = new Set<string>();
	return {
		schemaVersion: 1,
		collections: set.collections.map(collection => {
			if (
				!collection ||
				typeof collection.id !== 'string' ||
				!ID.test(collection.id) ||
				collections.has(collection.id) ||
				typeof collection.name !== 'string' ||
				!collection.name.trim() ||
				!Array.isArray(collection.tokens)
			)
				throw new Error('Collections require unique valid IDs, names and token arrays.');
			collections.add(collection.id);
			return {
				id: collection.id,
				name: collection.name,
				tokens: collection.tokens.map(token => {
					if (
						!token ||
						typeof token.id !== 'string' ||
						!ID.test(token.id) ||
						ids.has(token.id) ||
						references.has(tokenIdToCssVariable(token.id))
					)
						throw new Error('Token IDs and CSS references must be valid and unique.');
					if (
						typeof token.name !== 'string' ||
						!token.name.trim() ||
						(token.description !== undefined && typeof token.description !== 'string')
					)
						throw new Error('Tokens require a display name and optional text description.');
					ids.add(token.id);
					references.add(tokenIdToCssVariable(token.id));
					return {
						id: token.id,
						name: token.name,
						type: token.type,
						value: validateDesignModeValue(token.value, modeGroups, value =>
							normalizeDesignTokenValue(token.type, value)
						),
						...(token.description !== undefined ? { description: token.description } : {}),
					} as DesignToken;
				}),
			};
		}),
	};
}

export function validateDesignAssetSet(
	input: unknown,
	modeGroups: DesignModeGroup[] = DEFAULT_DESIGN_MODE_GROUPS
): DesignAssetSet {
	if (
		!input ||
		typeof input !== 'object' ||
		(input as DesignAssetSet).schemaVersion !== 1 ||
		!Array.isArray((input as DesignAssetSet).assets)
	)
		throw new Error('An asset set requires schemaVersion: 1 and an assets array.');
	const ids = new Set<string>();
	return {
		schemaVersion: 1,
		assets: (input as DesignAssetSet).assets.map(asset => {
			if (
				!asset ||
				typeof asset.id !== 'string' ||
				!ID.test(asset.id) ||
				ids.has(asset.id) ||
				typeof asset.name !== 'string' ||
				!asset.name.trim() ||
				asset.type !== 'image' ||
				!['logo', 'compactLogo', 'share', 'favicon', 'other'].includes(asset.role) ||
				(asset.description !== undefined && typeof asset.description !== 'string')
			)
				throw new Error('Assets require unique valid IDs, names, roles and image values.');
			ids.add(asset.id);
			return {
				id: asset.id,
				name: asset.name,
				role: asset.role,
				type: 'image',
				value: validateDesignModeValue(asset.value, modeGroups, value => {
					if (typeof value !== 'string') throw new Error('Image asset values must be strings.');
					return value;
				}),
				...(asset.description !== undefined ? { description: asset.description } : {}),
			} as DesignAsset;
		}),
	};
}

export function createDesignDefinition(
	fontFamily = SYSTEM_DEFAULT_FONT_FAMILY,
	tokens: DesignTokenSet = EMPTY_DESIGN_TOKENS,
	modeGroups: DesignModeGroup[] = DEFAULT_DESIGN_MODE_GROUPS,
	assets: DesignAssetSet = EMPTY_DESIGN_ASSETS
): DesignDefinition {
	const groups = validateDesignModeGroups(modeGroups);
	return {
		schemaVersion: 1,
		modeGroups: groups,
		defaults: { root: { fontFamily: createDesignModeValue(normalizeDefaultFontFamily(fontFamily)) } },
		tokens: copyDesignTokens(tokens, groups),
		assets: copyDesignAssets(assets, groups),
	};
}

export function validateDesignDefinition(input: unknown): DesignDefinition {
	if (!input || typeof input !== 'object' || (input as DesignDefinition).schemaVersion !== 1)
		throw new Error('A design definition requires schemaVersion: 1.');
	const definition = input as DesignDefinition;
	const modeGroups = validateDesignModeGroups(definition.modeGroups);
	const fontFamily = validateDesignModeValue(definition.defaults?.root?.fontFamily, modeGroups, value => {
		if (typeof value !== 'string' || normalizeDefaultFontFamily(value) !== value.trim())
			throw new Error('Design defaults require valid root font families.');
		return value.trim();
	});
	return {
		schemaVersion: 1,
		modeGroups,
		defaults: { root: { fontFamily } },
		tokens: copyDesignTokens(definition.tokens, modeGroups),
		assets: copyDesignAssets(definition.assets, modeGroups),
		...(definition.presets !== undefined ? { presets: validateDesignPresets(definition.presets) } : {}),
	};
}

export function copyDesignDefinition(design: DesignDefinition): DesignDefinition {
	return validateDesignDefinition(design);
}

export function areDesignDefinitionsEqual(before?: DesignDefinition, after?: DesignDefinition): boolean {
	try {
		return isEqual(copyDesignDefinition(before!), copyDesignDefinition(after!));
	} catch {
		return false;
	}
}

export function createDefaultFontFamilyCss(design?: DesignDefinition): string {
	if (!design) return `body {\n\t--sc-default-font-family: ${SYSTEM_DEFAULT_FONT_FAMILY};\n}`;
	const value = design.defaults.root.fontFamily;
	const baseSelection = getDefaultDesignModeSelection(design.modeGroups);
	const declaration = (selection: DesignModeSelection) =>
		`\t--sc-default-font-family: ${normalizeDefaultFontFamily(
			resolveDesignModeValue(value, { ...baseSelection, ...selection })
		)};`;
	const rules = [`body {\n${declaration({})}\n}`];
	const affectedGroups = new Set((value.overrides ?? []).flatMap(override => Object.keys(override.when)));
	for (const groupId of affectedGroups) {
		const group = design.modeGroups.find(item => item.id === groupId)!;
		for (const mode of group.modes)
			rules.push(`[data-sc-mode-${groupId}="${mode.id}"] {\n${declaration({ [groupId]: mode.id })}\n}`);
	}
	for (const override of value.overrides ?? []) {
		if (Object.keys(override.when).length <= 1) continue;
		const selector = Object.entries(override.when)
			.map(([groupId, modeId]) => `[data-sc-mode-${groupId}="${modeId}"]`)
			.join('');
		rules.push(`${selector} {\n${declaration(override.when)}\n}`);
	}
	return rules.join('\n');
}

export function copyDesignTokens(
	tokens?: DesignTokenSet,
	modeGroups: DesignModeGroup[] = DEFAULT_DESIGN_MODE_GROUPS
): DesignTokenSet {
	return validateDesignTokenSet(tokens ?? EMPTY_DESIGN_TOKENS, modeGroups);
}
export function copyDesignAssets(
	assets?: DesignAssetSet,
	modeGroups: DesignModeGroup[] = DEFAULT_DESIGN_MODE_GROUPS
): DesignAssetSet {
	return validateDesignAssetSet(assets ?? EMPTY_DESIGN_ASSETS, modeGroups);
}

export function importDesignAssets(
	story: DesignDefinition,
	library: DesignDefinition,
	assetIds: string[]
): DesignDefinition {
	const result = copyDesignDefinition(story);
	const source = copyDesignDefinition(library);
	for (const id of assetIds) {
		const asset = source.assets.assets.find(item => item.id === id);
		if (!asset) throw new Error(`Unknown Brand Kit asset: ${id}.`);
		// Story snapshots own their values; an existing ID always keeps its local definition.
		if (!result.assets.assets.some(item => item.id === id)) result.assets.assets.push(asset);
	}
	// Imported conditions must be valid in the Story's mode groups; never change runtime activation on import.
	return copyDesignDefinition(result);
}
export function areDesignTokenSetsEqual(before?: DesignTokenSet, after?: DesignTokenSet): boolean {
	try {
		return isEqual(copyDesignTokens(before), copyDesignTokens(after));
	} catch {
		return false;
	}
}
export function createStoryTokenSnapshot(
	libraryTokens?: DesignTokenSet,
	templateTokens?: DesignTokenSet
): DesignTokenSet {
	return copyDesignTokens(templateTokens ?? libraryTokens);
}
export function importDesignTokens(story: DesignTokenSet, library: DesignTokenSet, tokenIds: string[]): DesignTokenSet {
	const result = copyDesignTokens(story);
	const source = validateDesignTokenSet(library);
	for (const id of tokenIds) {
		const collection = source.collections.find(c => c.tokens.some(t => t.id === id));
		const token = collection?.tokens.find(t => t.id === id);
		if (!collection || !token) throw new Error(`Unknown Brand Kit token: ${id}.`);
		const existing = result.collections.flatMap(c => c.tokens).find(t => t.id === id);
		if (existing) {
			if (existing.type !== token.type) throw new Error(`Token type conflict: ${id}.`);
			continue;
		}
		let target = result.collections.find(c => c.id === collection.id);
		if (!target) {
			target = { id: collection.id, name: collection.name, tokens: [] };
			result.collections.push(target);
		}
		target.tokens.push({
			...token,
			value: {
				...token.value,
				...(token.value.overrides
					? {
							overrides: token.value.overrides.map(override => ({
								...override,
								when: { ...override.when },
							})),
						}
					: {}),
			},
		} as DesignToken);
	}
	return result;
}
export function setDesignTokenValue(set: DesignTokenSet, id: string, value: unknown): DesignTokenSet {
	const result = copyDesignTokens(set);
	const token = result.collections.flatMap(c => c.tokens).find(t => t.id === id);
	if (!token) throw new Error(`Unknown Story token: ${id}.`);
	token.value.base = normalizeDesignTokenValue(token.type, value) as never;
	return result;
}
export function createDesignTokenId(name: string, existing: string[], fallback = 'token'): string {
	const base = name
		.replace(/([a-z0-9])([A-Z])/g, '$1-$2')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
	const seed = /^[a-z]/.test(base) ? base : fallback;
	let id = seed;
	let suffix = 2;
	while (existing.includes(id)) {
		id = `${seed}-${suffix}`;
		suffix += 1;
	}
	return id;
}
